import React, { useState, useEffect, useCallback } from 'react';
import { Table, Select, Button, Space, Spin, Empty, Tooltip } from 'antd';
import { ReloadOutlined, BarChartOutlined, LineChartOutlined } from '@ant-design/icons';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip,
    Legend, ResponsiveContainer,
} from 'recharts';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import TableExportButton from '../../components/TableExportButton';
import PageHeader from '../../components/PageHeader';

const ANNEES = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

// ── Helpers ────────────────────────────────────────────────────────────────
const fNb = (v) => (v ?? 0).toLocaleString(localeIntl());
const fPct = (v) => v == null ? '-' : `${v > 0 ? '+' : ''}${v.toFixed(1)} %`;
const varColor = (v) => v == null ? '#6b7280' : v > 0 ? '#166534' : v < 0 ? '#991b1b' : '#374151';

// ── Colonnes tableau ───────────────────────────────────────────────────────
function buildCols(annee, t) {
    return [
        {
            title: t('sinistres.colonnes.paysPartenaire'), dataIndex: 'bureau', key: 'bureau', width: 170, fixed: 'left',
            render: (v, r) => (
                <span style={{
                    fontWeight: r.bureau === 'TOTAL' ? 800 : 600,
                    color: r.bureau === 'TOTAL' ? '#1a6b3c' : '#111827',
                }}>{v?.toUpperCase()}</span>
            ),
        },
        {
            title: String(annee - 1), dataIndex: 'nbN1', key: 'nbN1', align: 'right', width: 110,
            render: (v, r) => <span style={{ fontWeight: r.bureau === 'TOTAL' ? 700 : 400 }}>{fNb(v)}</span>,
        },
        {
            title: String(annee), dataIndex: 'nbN', key: 'nbN', align: 'right', width: 110,
            render: (v, r) => (
                <span style={{
                    fontWeight: r.bureau === 'TOTAL' ? 800 : 600,
                    color: r.bureau === 'TOTAL' ? '#1a6b3c' : '#111827',
                }}>{fNb(v)}</span>
            ),
        },
        {
            title: t('sinistres.colonnes.variation', { annee, anneePrecedente: annee - 1 }), dataIndex: 'variation', key: 'var', align: 'right', width: 130,
            render: (v) => <span style={{ fontWeight: 600, color: varColor(v) }}>{fPct(v)}</span>,
            export: (v) => v == null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)} %`,
        },
        {
            title: `% ${annee}`, dataIndex: 'pourcentage', key: 'pct', align: 'right', width: 100,
            render: (v) => v == null ? '-' : (
                <span style={{ fontWeight: 600, color: '#374151' }}>{v.toFixed(1)} %</span>
            ),
            export: (v) => v == null ? '' : `${v.toFixed(1)} %`,
        },
    ];
}

// ── Tooltip personnalisé pour le graphe ────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
            padding: '10px 14px', fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,.08)',
        }}>
            <p style={{ fontWeight: 700, marginBottom: 6, color: '#111827' }}>{label}</p>
            {payload.map((p) => (
                <p key={p.dataKey} style={{ color: p.color, margin: '2px 0' }}>
                    {p.name?.toUpperCase()} : <strong>{p.value.toLocaleString(localeIntl())}</strong>
                </p>
            ))}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
//  COMPOSANT
// ════════════════════════════════════════════════════════════════════════════
export default function EtatSinistres() {
    const { t } = useTranslation('statistiques');
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/stats/sinistres?annee=${annee}`);
            setData(res.data?.data);
        } catch {
            toast.error(t('commun.erreurChargementStatistiques'));
        } finally {
            setLoading(false);
        }
    }, [annee, t]);

    useEffect(() => { load(); }, [load]);

    const tableData = data
        ? [
            ...(data.lignes || []).map((r, i) => ({ ...r, key: i })),
            { ...(data.total || {}), key: 'total', _isTotal: true },
        ]
        : [];

    const cols = buildCols(annee, t);

    // Préparer les données graphe : convertir les nombres (JDBC renvoie BigDecimal/Long)
    const evolutionData = (data?.evolution || []).map((r) => ({
        annee: Number(r.annee),
        total: Number(r.total),
        et: Number(r.et),
        te: Number(r.te),
    }));

    return (
        <Layout title={t('sinistres.titreOnglet')}>
            <div style={{ maxWidth: 960 }}>

                {/* Compte : `data.lignes` seul - `tableData` y ajoute la ligne TOTAL,
                    qui est une synthese et non un pays partenaire. */}
                <PageHeader
                    titre={t('sinistres.titre')}
                    icone={<BarChartOutlined />}
                    compte={data ? (data.lignes || []).length : undefined}
                    unite={t('commun.paysPartenaire')}
                    unitePluriel={t('commun.paysPartenaires')}
                    precision={data?.dateArrete ? t('commun.arreteAu', { date: data.dateArrete }) : undefined}
                    actions={(
                        <Space>
                            <Select
                                value={annee}
                                onChange={setAnnee}
                                style={{ width: 100, borderRadius: 8 }}
                                options={ANNEES.map((a) => ({ value: a, label: a }))}
                            />
                            <Tooltip title={t('commun.actualiser')}>
                                <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 8 }} />
                            </Tooltip>
                            <TableExportButton
                              floating
                                data={tableData}
                                columns={cols}
                                filename={`etat-sinistres-${annee}`}
                                title={t('sinistres.export.titre', { annee })}
                                subtitle={t('sinistres.export.sousTitre', { annee, anneePrecedente: annee - 1 })}
                                disabled={!data}
                            />
                        </Space>
                    )}
                />

                {/* ── Tableau ──────────────────────────────────────────── */}
                <div className="cb-card" style={{ padding: 0 }}>
                    <div style={{
                        padding: '16px 20px 8px', borderBottom: '1px solid #f3f4f6',
                        display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <BarChartOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
                        <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 14 }}>
                            {t('sinistres.titreTableau', { annee })}
                        </span>
                    </div>
                    <Spin spinning={loading}>
                        {!loading && tableData.length === 0 ? (
                            <Empty description={t('commun.aucuneDonnee')} style={{ padding: 40 }} />
                        ) : (
                            <Table
                                className="cb-table"
                                dataSource={tableData}
                                columns={cols}
                                pagination={false}
                                size="small"
                                scroll={{ x: 620 }}
                                rowClassName={(r) => r._isTotal ? 'ant-table-row-total' : ''}
                                summary={() => null}
                            />
                        )}
                    </Spin>
                </div>

                {/* ── Graphe d'évolution ───────────────────────────────── */}
                {evolutionData.length > 0 && (
                    <div className="cb-card" style={{ padding: 0, marginTop: 24 }}>
                        <div style={{
                            padding: '16px 20px 8px', borderBottom: '1px solid #f3f4f6',
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            <LineChartOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
                            <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 14 }}>
                                {t('sinistres.titreEvolution')}
                            </span>
                        </div>
                        <div style={{ padding: '24px 16px 16px' }}>
                            <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={evolutionData} margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="annee"
                                        tick={{ fontSize: 12, fill: '#6b7280' }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12, fill: '#6b7280' }}
                                        tickLine={false}
                                        axisLine={false}
                                        allowDecimals={false}
                                    />
                                    <RechartTooltip content={<CustomTooltip />} />
                                    <Legend
                                        wrapperStyle={{ paddingTop: 12, fontSize: 13 }}
                                        formatter={(value) => ({
                                            total: t('sinistres.legende.total'),
                                            et: t('sinistres.legende.et'),
                                            te: t('sinistres.legende.te'),
                                        }[value] || value)}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="total"
                                        name="total"
                                        stroke="#1a6b3c"
                                        strokeWidth={2.5}
                                        dot={{ r: 4, fill: '#1a6b3c' }}
                                        activeDot={{ r: 6 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="et"
                                        name="et"
                                        stroke="#2563eb"
                                        strokeWidth={2}
                                        strokeDasharray="6 3"
                                        dot={{ r: 3, fill: '#2563eb' }}
                                        activeDot={{ r: 5 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="te"
                                        name="te"
                                        stroke="#163d28"
                                        strokeWidth={2}
                                        strokeDasharray="3 3"
                                        dot={{ r: 3, fill: '#163d28' }}
                                        activeDot={{ r: 5 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>

                            {/* Légende explicative */}
                            <div style={{
                                marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap',
                                fontSize: 11, color: '#6b7280', padding: '0 8px',
                            }}>
                                <span>
                                    <span style={{ display: 'inline-block', width: 20, height: 3, background: '#2563eb', marginRight: 6, verticalAlign: 'middle', borderRadius: 2 }} />
                                    <Trans t={t} i18nKey="sinistres.legende.etDetail" components={{ strong: <strong /> }} />
                                </span>
                                <span>
                                    <span style={{ display: 'inline-block', width: 20, height: 3, background: '#163d28', marginRight: 6, verticalAlign: 'middle', borderRadius: 2 }} />
                                    <Trans t={t} i18nKey="sinistres.legende.teDetail" components={{ strong: <strong /> }} />
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Source */}
                {data && (
                    <div style={{ marginTop: 12, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
                        {t('commun.sourceGenereLe', { date: new Date().toLocaleDateString(localeIntl()) })}
                    </div>
                )}
            </div>

            <style>{`
                .ant-table-row-total td { background: #f0faf4 !important; font-weight: 700 !important; }
                .ant-table-row-total:hover td { background: #dcfce7 !important; }
            `}</style>
        </Layout>
    );
}
